import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, Reaction, ReactionKind } from '@xdocs/schemas';
import { useToast } from '@xdocs/ui';
import { useWs } from '../../ws/WsProvider.js';
import { useSharingT } from './useSharingT.js';

export interface ReactionCounts {
  likeCount: number;
  dislikeCount: number;
  viewerReaction: ReactionKind | null;
}

export interface UseReactionResult extends ReactionCounts {
  isPending: boolean;
  /** Re-clicking the CURRENTLY active kind clears the reaction (`kind: null`). */
  toggle: (kind: ReactionKind) => void;
}

function applyTransition(prev: ReactionCounts, kind: ReactionKind | null): ReactionCounts {
  let { likeCount, dislikeCount } = prev;
  if (prev.viewerReaction === 'like') likeCount -= 1;
  if (prev.viewerReaction === 'dislike') dislikeCount -= 1;
  if (kind === 'like') likeCount += 1;
  if (kind === 'dislike') dislikeCount += 1;
  return { likeCount, dislikeCount, viewerReaction: kind };
}

/**
 * `reaction.set` (F29) with an optimistic local override reconciled against
 * the live `server` counts (the caller's own query data, kept fresh by the
 * `share:{shareId}` push → `['share', shareId]` invalidation already wired
 * in `ws/queryInvalidation.ts`) — mirrors `useSearchQuery`'s suppression
 * mutation's onMutate/onError/reconcile shape (E19 mutation states).
 */
export function useReaction(shareId: string, server: ReactionCounts): UseReactionResult {
  const { wsClient } = useWs();
  const toast = useToast();
  const { t } = useSharingT();
  const [optimistic, setOptimistic] = useState<ReactionCounts | null>(null);

  // Drop the optimistic override the moment the server's own viewerReaction
  // catches up to what it was predicting — never let a stale override
  // outlive the real state indefinitely.
  useEffect(() => {
    setOptimistic((prev) => (prev && prev.viewerReaction === server.viewerReaction ? null : prev));
  }, [server.viewerReaction]);

  const mutation = useMutation<Reaction, ErrorAlert, ReactionKind | null, { previous: ReactionCounts }>({
    mutationFn: (kind) => requestTyped(wsClient, 'reaction.set', { shareId, kind }),
    onMutate: (kind) => {
      const previous = optimistic ?? server;
      setOptimistic(applyTransition(previous, kind));
      return { previous };
    },
    onError: (_err, _kind, context) => {
      setOptimistic(context?.previous ?? null);
      toast.push({ tone: 'error', message: t('sharing:reaction.setFailed') });
    },
  });

  const display = optimistic ?? server;

  return {
    ...display,
    isPending: mutation.isPending,
    toggle: (kind) => mutation.mutate(display.viewerReaction === kind ? null : kind),
  };
}
